import React from 'react'

import mq from '@/utils/media-query'

import type {Breakpoints} from '@/utils/media-query'

/**
 * Allows to detect and monitor if a given media query string applies
 * @param query Media query string to check the match for
 * @returns A flag indicating if the document matches the query string
 * @example
 * const isSmallScreen = useMediaQuery('(min-width: 768px)')
 */
export const useMediaQuery = (query: string) => {
    const [queryMatches, setQueryMatches] = React.useState(false)

    React.useLayoutEffect(() => {
        let mounted = true
        const mediaQueryList = window.matchMedia(query.replace(/^@media /, ''))

        const onChange = () => {
            if (!mounted) {
                return
            }

            setQueryMatches(Boolean(mediaQueryList.matches))
        }

        mediaQueryList.addListener(onChange)
        setQueryMatches(mediaQueryList.matches)

        return () => {
            mounted = false
            mediaQueryList.removeListener(onChange)
        }
    }, [query])

    return queryMatches
}

/**
 * Wrappers around useMediaQuery to work with our media query helper
 * @example
 * const isSmallScreen = useMediaQuery.min('sm')
 */
const useMinMediaQuery = (bp: keyof Breakpoints) => useMediaQuery(mq.min(bp))
const useMaxMediaQuery = (bp: keyof Breakpoints) => useMediaQuery(mq.max(bp))
const useBetweenMediaQuery = (bp1: keyof Breakpoints, bp2: keyof Breakpoints) =>
    useMediaQuery(mq.between(bp1, bp2))

export default {
    min: useMinMediaQuery,
    max: useMaxMediaQuery,
    between: useBetweenMediaQuery,
}
